/**
 * CreditNotesList — list all credit notes for an account + PDF + settle.
 *
 * Settlement lifecycle (migration 0099):
 *   Issued (default) → Refunded (terminal) | Adjusted (pending) → Applied
 * Operator can move Issued → Refunded or Adjusted via inline buttons.
 * Refunded/Applied are terminal — no further actions surface.
 */

import {
  useListAccountCreditNotes,
  useRegenerateCreditNotePdf,
  useSettleCreditNote,
  getListAccountCreditNotesQueryKey,
  type CreditNote,
} from "@workspace/api-client-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Download, Loader2, ExternalLink } from "lucide-react";
import { useState } from "react";
import { MarkRefundedDialog } from "./mark-refunded-dialog";

/** INR formatter. */
function fmt(n: number) {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

function statusLabel(s: string): string {
  switch (s) {
    case "Refunded":
      return "Refunded";
    case "Adjusted":
      return "Adjusted (pending)";
    case "Applied":
      return "Applied";
    default:
      return "Issued";
  }
}

function statusClass(s: string): string {
  switch (s) {
    case "Refunded":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "Adjusted":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "Applied":
      return "bg-sky-100 text-sky-800 border-sky-200";
    default:
      return "";
  }
}

interface CreditNotesListProps {
  accountId: number;
}

export function CreditNotesList({ accountId }: CreditNotesListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: creditNotes, isLoading } = useListAccountCreditNotes(accountId, {
    query: {
      enabled: accountId > 0,
      queryKey: getListAccountCreditNotesQueryKey(accountId),
    },
  });

  const regeneratePdf = useRegenerateCreditNotePdf();
  const settleMutation = useSettleCreditNote();
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [refundTarget, setRefundTarget] = useState<CreditNote | null>(null);
  const [settlingId, setSettlingId] = useState<number | null>(null);

  async function onRegeneratePdf(creditNoteId: number, existingUrl?: string | null) {
    if (existingUrl) {
      window.open(existingUrl, "_blank", "noopener");
      return;
    }
    setGeneratingId(creditNoteId);
    try {
      const r = await regeneratePdf.mutateAsync({ id: creditNoteId });
      if (r.pdfUrl) {
        window.open(r.pdfUrl, "_blank", "noopener");
      } else {
        toast({
          title: "PDF generation succeeded but no URL was returned.",
          variant: "destructive",
        });
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "";
      toast({
        title: "Couldn't generate the credit-note PDF.",
        description: msg || undefined,
        variant: "destructive",
      });
    } finally {
      setGeneratingId(null);
    }
  }

  async function onMarkAdjusted(creditNoteId: number) {
    setSettlingId(creditNoteId);
    try {
      await settleMutation.mutateAsync({
        id: creditNoteId,
        data: { settlementStatus: "Adjusted" },
      });
      queryClient.invalidateQueries({ queryKey: getListAccountCreditNotesQueryKey(accountId) });
      toast({
        title: "Marked Adjusted.",
        description: "Will auto-apply to the customer's next plan.",
      });
    } catch (err) {
      toast({
        title: "Couldn't update the credit note.",
        description: (err as Error)?.message ?? undefined,
        variant: "destructive",
      });
    } finally {
      setSettlingId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2 pt-2">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded" />
        ))}
      </div>
    );
  }

  if (!creditNotes || creditNotes.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No credit notes issued for this account yet.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Credit Note #</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Applied to Invoice</TableHead>
            <TableHead className="text-right">Total Reversed</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Settlement</TableHead>
            <TableHead className="w-24 text-right">PDF</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {creditNotes.map((cn) => {
            const status = cn.settlementStatus ?? "Issued";
            const terminal = status === "Refunded" || status === "Applied";
            return (
              <TableRow key={cn.id}>
                <TableCell className="font-mono text-sm font-medium">
                  {cn.creditNoteNumber}
                </TableCell>
                <TableCell className="text-sm">
                  {cn.membershipId != null ? (
                    <span className="font-semibold">Plan #{cn.membershipId}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(cn.creditNoteDate), "dd MMM yyyy")}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {cn.appliedToInvoiceNumber ?? "—"}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  {fmt(cn.totalReversed)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${statusClass(status)}`}
                  >
                    {statusLabel(status)}
                  </Badge>
                  {status === "Refunded" && cn.settlementDate && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {cn.settlementPaymentMode ? `${cn.settlementPaymentMode} · ` : ""}
                      {format(new Date(cn.settlementDate), "dd MMM yyyy")}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {terminal ? (
                    status === "Refunded" && cn.settlementProofUrl ? (
                      <a
                        href={cn.settlementProofUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        Proof
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )
                  ) : status === "Adjusted" ? (
                    <span className="text-[10px] text-muted-foreground">
                      Awaiting next plan
                    </span>
                  ) : (
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setRefundTarget(cn)}
                        disabled={settlingId === cn.id}
                      >
                        Mark Refunded
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => onMarkAdjusted(cn.id)}
                        disabled={settlingId === cn.id}
                      >
                        {settlingId === cn.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          "Mark Adjusted"
                        )}
                      </Button>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 text-xs"
                    disabled={generatingId === cn.id}
                    onClick={() => onRegeneratePdf(cn.id, cn.pdfUrl)}
                  >
                    {generatingId === cn.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    PDF
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MarkRefundedDialog
        accountId={accountId}
        creditNote={refundTarget}
        open={refundTarget != null}
        onOpenChange={(o) => { if (!o) setRefundTarget(null); }}
      />
    </div>
  );
}
